import { ReactNode } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { FORM_STATUS_ENUM } from "@interfaces/response";
import { ChipEstadoExpediente } from "./ChipEstadoExpediente";

interface Props {
  index?: number;
  title: ReactNode;
  /** Resumen bajo el título; el diseño lo deja visible también con la sección abierta. */
  summary?: string;
  status?: FORM_STATUS_ENUM;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

/**
 * Sección plegable del expediente (`.ref-card` del diseño): encabezado con el número, el título,
 * su resumen y el estado, y el cuerpo separado por una línea punteada.
 *
 * Abierta se despega del fondo —borde más firme y sombra— y el número pasa a navy, que es como el
 * diseño marca la sección en la que estás.
 */
export const AccordionCard = ({ index, title, summary, status, open, onToggle, children }: Props) => {
  // El número va relleno tanto en la sección abierta como en la ya resuelta.
  const numeroRelleno = open || status === "COMPLETO" || status === "VALIDADO";

  return (
    <Paper
      variant="lendia_paper"
      sx={{
        p: 0,
        overflow: "hidden",
        transition: "border-color .15s, box-shadow .15s",
        ...(open
          ? { borderColor: "tokens.borderStrong", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)" }
          : { boxShadow: "none" }),
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        sx={{
          all: "unset",
          boxSizing: "border-box",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "14px",
          p: "14px 18px",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0, flex: 1 }}>
          {index != null && (
            <Typography
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                border: "1px solid",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: 600,
                flexShrink: 0,
                ...(numeroRelleno
                  ? { bgcolor: "primary.dark", borderColor: "primary.dark", color: "#fff" }
                  : { bgcolor: "tokens.surface2", borderColor: "tokens.borderStrong", color: "#4A5567" }),
              }}
            >
              {index}
            </Typography>
          )}

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
              {title}
            </Typography>
            {summary && (
              <Typography noWrap sx={{ fontSize: 12, color: "#7A8497", mt: "2px" }}>
                {summary}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
          {status && <ChipEstadoExpediente formStatus={status} />}
          <ExpandMore
            sx={{
              fontSize: 18,
              color: "text.secondary",
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform .15s",
            }}
          />
        </Box>
      </Box>

      {open && (
        <Box sx={{ p: "6px 18px 18px", borderTop: "1px dashed", borderTopColor: "tokens.borderStrong", mt: "-1px" }}>
          {children}
        </Box>
      )}
    </Paper>
  );
};
